import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box
} from '@mui/material';
import { AdviseTask, AdviseEngagement } from '../../../types/advise';

interface TaskFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (task: Partial<AdviseTask>) => void;
  task: AdviseTask | null;
  engagements: AdviseEngagement[];
}

const emptyForm = {
  title: '',
  description: '',
  status: 'todo',
  priority: 'medium',
  due_date: '',
  engagement_id: ''
};

export const TaskFormDialog = ({ open, onClose, onSubmit, task, engagements }: TaskFormDialogProps) => {
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (task) {
      setForm({
        title: task.title || '',
        description: task.description || '',
        status: task.status || 'todo',
        priority: task.priority || 'medium',
        due_date: task.due_date ? task.due_date.slice(0, 10) : '',
        engagement_id: task.engagement_id || ''
      });
    } else {
      setForm(emptyForm);
    }
  }, [task, open]);

  const handleChange = useCallback((key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!form.title.trim()) return;
    onSubmit(form);
    setForm(emptyForm);
  }, [form, onSubmit]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth>
      <DialogTitle>{task ? 'Edit Task' : 'New Task'}</DialogTitle>
      <DialogContent>
        <Box className="flex flex-col gap-3 mt-2">
          <TextField
            label="Title"
            value={form.title}
            onChange={(e) => handleChange('title', e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
          <Box className="grid grid-cols-3 gap-3">
            <FormControl
              size="small"
              fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={form.priority}
                label="Priority"
                onChange={(e) => handleChange('priority', e.target.value)}>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>
            <FormControl
              size="small"
              fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={form.status}
                label="Status"
                onChange={(e) => handleChange('status', e.target.value)}>
                <MenuItem value="todo">To Do</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="done">Done</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Due Date"
              type="date"
              size="small"
              value={form.due_date}
              onChange={(e) => handleChange('due_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
          <FormControl
            size="small"
            fullWidth>
            <InputLabel>Link to Engagement</InputLabel>
            <Select
              value={form.engagement_id}
              label="Link to Engagement"
              onChange={(e) => handleChange('engagement_id', e.target.value)}>
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {engagements.map((c) => (
                <MenuItem
                  key={c.id}
                  value={c.id}>
                  {c.engagement_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!form.title.trim()}>
          {task ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
